/**
 * Phase 27.50.B.16 — Marketing Center shell.
 *
 * Post ecommerce pivot, the Marketing Center is the single home for
 * every marketing-adjacent surface:
 *   1. Shop            — storefront (replaces the legacy Materials tab)
 *   2. Cart            — active cart contents
 *   3. My Orders       — history + reorder + receipts
 *   4. Event Requests  — approval workflow
 *   5. Retail Program  — retail submissions
 *   6. Event Reports   — post-event metrics (KR / non-agent only)
 *
 * The active tab is mirrored in the URL query string (`?tab=…`) so
 * deep links + refreshes restore the same view.
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { isKRRole, isAgent } from '@/lib/utils';
import useCartCount from '@/hooks/useCartCount';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Plus, Inbox, ShoppingBag, ShoppingCart, Package,
  CalendarClock, BarChart3, Store, Megaphone, History, Boxes, ClipboardList,
} from 'lucide-react';
import ShopPage from '../shop/ShopPage';
import CartPage from '../shop/CartPage';
import MyOrdersPage from '../shop/MyOrdersPage';
import EventRequestsTab from './EventRequestsTab';
import RetailProgramTab from './RetailProgramTab';
import EventReportsTab from './EventReportsTab';
import BroadcastCenterTab from './BroadcastCenterTab';
import EquipmentRequestsTab from '@/components/supplies/RequestsTab';
import { OrdersTab as ShopOrdersAdminTab } from '@/pages/admin/AdminStorefrontPage';
import MarketingPromoDialog from '@/components/marketing/MarketingPromoDialog';
import PromoHistoryDialog from '@/components/marketing/PromoHistoryDialog';

const VALID_TABS = ['shop', 'cart', 'my-orders', 'shop-orders', 'event-requests', 'retail-program', 'equipment-requests', 'event-reports', 'broadcast'];

export default function MarketingPage() {
  const { user } = useAuth();
  const isAdmin = isKRRole(user);
  // Event Reports are only visible to KR admins and agency
  // admins/principals. Individual agents don't get the tab.
  const canSeeReports = !isAgent(user);
  const cartCount = useCartCount();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const initialTab = VALID_TABS.includes(requestedTab)
    ? requestedTab : 'shop';
  const safeInitialTab = (initialTab === 'event-reports' && !canSeeReports) ||
    (initialTab === 'shop-orders' && !isAdmin)
    ? (initialTab === 'shop-orders' ? 'shop' : 'event-requests') : initialTab;
  const [tab, setTab] = useState(safeInitialTab);
  const [promoOpen, setPromoOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Keep the query string in sync so refresh + deep-links keep state.
  useEffect(() => {
    if (searchParams.get('tab') !== tab) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', tab);
      setSearchParams(next, { replace: true });
    }
  }, [tab, searchParams, setSearchParams]);

  return (
    <div data-testid="marketing-page">
      <PageHeader
        title="Marketing Center"
        description="Storefront, event requests, retail submissions, and post-event effectiveness — all in one place."
        actions={
          tab === 'shop' && isAdmin ? (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setHistoryOpen(true)}
                data-testid="promo-history-open-btn"
              >
                <History size={16} className="mr-2" />Promo History
              </Button>
              <Button
                variant="outline"
                onClick={() => setPromoOpen(true)}
                data-testid="promo-open-btn"
              >
                <Megaphone size={16} className="mr-2" />Send Promo
              </Button>
              <Link to="/marketing/requests">
                <Button variant="outline" data-testid="marketing-requests-btn">
                  <Inbox size={16} className="mr-2" />Order Queue
                </Button>
              </Link>
              <Link to="/marketing/new">
                <Button data-testid="upload-template-btn">
                  <Plus size={16} className="mr-2" />Upload Template
                </Button>
              </Link>
            </div>
          ) : null
        }
      />

      <div className="page-content">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="mb-6" data-testid="marketing-tabs">
            <TabsTrigger value="shop" data-testid="tab-shop">
              <ShoppingBag size={14} className="mr-1.5" /> Shop
            </TabsTrigger>
            <TabsTrigger value="cart" data-testid="tab-cart">
              <ShoppingCart size={14} className="mr-1.5" /> Cart
              {cartCount > 0 && (
                <span
                  data-testid="tab-cart-count"
                  title={`${cartCount} item${cartCount === 1 ? '' : 's'} in your cart`}
                  className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold leading-none"
                >
                  {cartCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="my-orders" data-testid="tab-my-orders">
              <Package size={14} className="mr-1.5" /> My Orders
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="shop-orders" data-testid="tab-shop-orders">
                <ClipboardList size={14} className="mr-1.5" /> Shop Orders
              </TabsTrigger>
            )}
            <TabsTrigger value="event-requests" data-testid="tab-event-requests">
              <CalendarClock size={14} className="mr-1.5" /> Event Requests
            </TabsTrigger>
            <TabsTrigger value="retail-program" data-testid="tab-retail-program">
              <Store size={14} className="mr-1.5" /> Retail Program
            </TabsTrigger>
            <TabsTrigger value="equipment-requests" data-testid="tab-equipment-requests">
              <Boxes size={14} className="mr-1.5" /> Event Equipment
            </TabsTrigger>
            {canSeeReports && (
              <TabsTrigger value="event-reports" data-testid="tab-event-reports">
                <BarChart3 size={14} className="mr-1.5" /> Event Reports
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="broadcast" data-testid="tab-broadcast">
                <Megaphone size={14} className="mr-1.5" /> Broadcast
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="shop">
            <ShopPage embedded />
          </TabsContent>
          <TabsContent value="cart">
            <CartPage embedded />
          </TabsContent>
          <TabsContent value="my-orders">
            <MyOrdersPage embedded />
          </TabsContent>
          {isAdmin && (
            <TabsContent value="shop-orders">
              <ShopOrdersAdminTab />
            </TabsContent>
          )}
          <TabsContent value="event-requests">
            <EventRequestsTab />
          </TabsContent>
          <TabsContent value="retail-program">
            <RetailProgramTab />
          </TabsContent>
          <TabsContent value="equipment-requests">
            <EquipmentRequestsTab />
          </TabsContent>
          {canSeeReports && (
            <TabsContent value="event-reports">
              <EventReportsTab />
            </TabsContent>
          )}
          {isAdmin && (
            <TabsContent value="broadcast">
              <BroadcastCenterTab />
            </TabsContent>
          )}
        </Tabs>
      </div>

      {isAdmin && (
        <>
          <MarketingPromoDialog
            open={promoOpen}
            onOpenChange={setPromoOpen}
            userEmail={user?.email || ''}
          />
          <PromoHistoryDialog
            open={historyOpen}
            onOpenChange={setHistoryOpen}
          />
        </>
      )}
    </div>
  );
}
